import { useState } from "react"
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute, redirect } from "@tanstack/react-router"
import { ArrowRight, Check, CheckCheck, CircleHelp, X } from "lucide-react"
import { toast } from "sonner"
import { CAPABILITY_LABELS, Capability, PROVIDER_LABELS, type ChannelConnection } from "@vienna/contracts"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Heading, MiniAvatar } from "@/components/workspace/primitives"
import { SetupSteps } from "@/components/workspace/setup-steps"
import { api, ApiError } from "@/lib/api"
import { attemptQuery, workspaceQuery } from "@/lib/queries"
import { useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/channels/connect/$attemptId")({
  beforeLoad: async ({ context, params }) => {
    const ctx = await context.queryClient.ensureQueryData(workspaceQuery(params.workspace))
    if (!ctx.permissions.includes("manage_workspace")) throw redirect({ to: "/w/$workspace/settings/channels", params })
  },
  loader: ({ context, params }) => context.queryClient.ensureQueryData(attemptQuery(params.workspace, params.attemptId)),
  component: ChannelConnect,
})

/** Screen 06. Account selection after provider authorisation, then the confirmation state. */
function ChannelConnect() {
  const { workspace } = useWorkspaceContext()
  const { attemptId } = Route.useParams()
  const queryClient = useQueryClient()
  const attempt = useSuspenseQuery(attemptQuery(workspace.slug, attemptId)).data
  const [selected, setSelected] = useState<string[]>(() => attempt.accounts.filter((a) => a.ready).map((a) => a.externalAccountId).slice(0, 1))
  const [connected, setConnected] = useState<ChannelConnection[]>([])
  const params = { workspace: workspace.slug }
  const label = PROVIDER_LABELS[attempt.provider]

  const connect = useMutation({
    mutationFn: () => api<{ connections: ChannelConnection[] }>(`/w/${workspace.slug}/channels/attempts/${attemptId}/connect`, { method: "POST", body: JSON.stringify({ externalAccountIds: selected }) }),
    onSuccess: async ({ connections }) => {
      setConnected(connections)
      await queryClient.invalidateQueries({ queryKey: ["workspace", workspace.slug, "channels"] })
      toast.success(`${connections.length} ${connections.length === 1 ? "account" : "accounts"} connected`)
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.problem.title : "Could not connect the accounts"),
  })

  const done = connected.length > 0 || attempt.status === "completed"
  const focus = attempt.accounts.find((a) => a.externalAccountId === selected[0]) ?? attempt.accounts[0]

  if (attempt.status === "failed" || attempt.status === "expired" || attempt.status === "pending") {
    return (
      <div className="page-wrap ws-workspace">
        <Heading title="The connection did not complete" description={attempt.error ?? (attempt.status === "expired" ? "The authorisation took too long. Start again." : "Authorisation has not finished yet. Complete it with the provider, then return here.")} />
        <SetupSteps active={1} />
        <div className="ws-form-footer">
          <Button variant="outline" asChild>
            <Link to="/w/$workspace/settings/channels" params={params}>
              View connections
            </Link>
          </Button>
          <Button asChild>
            <Link to="/w/$workspace/channels/new" params={params} search={{ provider: attempt.provider }}>
              Try again
              <ArrowRight />
            </Link>
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="page-wrap ws-workspace">
      <Heading
        title={done ? "Your account is connected" : "Choose the accounts to connect"}
        description={done ? `${workspace.name} is ready to receive customer conversations.` : "Authorization complete. Select the accounts your team will work with."}
      />
      <SetupSteps active={done ? 3 : 2} />
      <div className="ws-connect-grid ws-connect-accounts">
        <Card>
          <CardHeader>
            <div className="ws-flex-between">
              <CardTitle>{label} accounts</CardTitle>
              <Badge variant="secondary">
                <Check size={12} />
                Authorized
              </Badge>
            </div>
            <CardDescription>Connected through your Meta business account.</CardDescription>
          </CardHeader>
          <CardContent>
            {done ? (
              <div className="ws-connection-success">
                <div className="ws-confirm-icon">
                  <CheckCheck size={28} />
                </div>
                <h2>
                  {connected.length || attempt.connectedConnectionIds.length} {(connected.length || attempt.connectedConnectionIds.length) === 1 ? "account" : "accounts"} connected
                </h2>
                <p>New messages will appear in your team’s inbox. Review connection health any time in Settings.</p>
                <Button asChild>
                  <Link to="/w/$workspace/inbox" params={params}>
                    Open your inbox
                    <ArrowRight />
                  </Link>
                </Button>
                <Button variant="ghost" asChild>
                  <Link to="/w/$workspace/settings/channels" params={params}>
                    View connections
                  </Link>
                </Button>
              </div>
            ) : (
              <>
                {attempt.accounts.map((account) => {
                  const checked = selected.includes(account.externalAccountId)
                  const missing = Capability.options.filter((c) => account.capabilities[c] !== "available")
                  return (
                    <label key={account.externalAccountId} className={`ws-account-option ${checked ? "selected" : ""}`}>
                      <Checkbox checked={checked} onCheckedChange={(value) => setSelected(value ? [...selected, account.externalAccountId] : selected.filter((k) => k !== account.externalAccountId))} />
                      <MiniAvatar name={account.displayName} size="size-10" />
                      <div>
                        <strong>{account.displayName}</strong>
                        <p>{account.handle ?? "Facebook Page"}</p>
                        <span>{account.alreadyConnected ? "Already connected · reconnecting refreshes access" : missing.length ? `${missing.length === 1 ? (CAPABILITY_LABELS[missing[0]!] ?? "Access") : `${missing.length} permissions`} missing` : "All required access granted"}</span>
                      </div>
                      <Badge variant="outline">{account.ready ? "Ready" : "Limited"}</Badge>
                    </label>
                  )
                })}
                {attempt.accounts.length === 0 && <p className="ws-empty">No eligible accounts were shared during authorisation.</p>}
                <div className="ws-quiet-help">
                  <CircleHelp size={17} />
                  <div>
                    <strong>Missing an account?</strong>
                    <p>Review authorization and choose the correct business.</p>
                    <Link to="/w/$workspace/channels/new" params={params} search={{ provider: attempt.provider }}>
                      Review authorization
                      <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
                <div className="ws-form-footer">
                  <Button variant="outline" asChild>
                    <Link to="/w/$workspace/channels/new" params={params} search={{ provider: attempt.provider }}>
                      Back
                    </Link>
                  </Button>
                  <Button disabled={!selected.length || connect.isPending} onClick={() => connect.mutate()}>
                    Connect {selected.length || ""} selected {selected.length === 1 ? "account" : "accounts"}
                    <ArrowRight />
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
        <div>
          <h2 className="ws-aside-title">Account access</h2>
          <p className="ws-hint mb-6">{focus?.handle ?? focus?.displayName ?? label}</p>
          {Capability.options.map((capability) => {
            const state = focus?.capabilities[capability] ?? "missing"
            return (
              <div className="ws-permission-line" key={capability}>
                {state === "available" ? <Check size={16} /> : <X size={16} />}
                <div>
                  <strong>{CAPABILITY_LABELS[capability]}</strong>
                  <p>{state === "available" ? "Granted" : "Not granted"}</p>
                </div>
              </div>
            )
          })}
          <p className="ws-hint mt-6">If access changes, Vienna will flag the affected features and pause work that needs those permissions.</p>
        </div>
      </div>
    </div>
  )
}
